/**
 * Basket import / export client.
 *
 * Wraps three backend routes:
 *   GET  /baskets/{id}/export        → exportBasket()
 *   POST /baskets/import/preview     → previewImport()  (no DB writes)
 *   POST /baskets/import             → commitImport()   (atomic write)
 */
import apiClient from './apiClient';
import { withActingAsIfSuperAdmin } from './actingAs';
import { ApiResponse } from '../types';

/** Bundle shape v1.0 — kept loose intentionally. The backend is the
 *  schema authority via libs/shared/shared/services/basket_io.py. */
export interface BasketBundle {
  schema_version: string;
  exported_at: string;
  exported_by_user_id: string;
  basket: Record<string, unknown> & { basket_name?: string };
  strategies: Array<Record<string, unknown> & { strategy_name?: string }>;
}

/** Per-strategy preview row returned by /baskets/import/preview. */
export interface ImportPreviewStrategy {
  strategy_index: number;
  strategy_name: string;
  underlying: string;
  expiry_type?: string;
  product?: string;
  trading_type?: string;
  leg_count: number;
  /** False when the importer's tenant doesn't list this underlying;
   *  the dialog auto-unchecks + locks the row so user can't enable it. */
  supported: boolean;
}

export interface ImportPreview {
  schema_version: string;
  exported_at?: string;
  exported_by_user_id?: string;
  basket: Record<string, unknown> & {
    basket_name: string;
    /** Server-suggested unique name (auto-rename'd if collision). */
    suggested_name: string;
  };
  strategies: ImportPreviewStrategy[];
  summary: {
    total: number;
    supported: number;
    skipped_unsupported: number;
  };
}

export interface ImportCommitResult {
  basket_id: string;
  basket_name: string;
  strategy_count: number;
  skipped_count: number;
  warnings: Array<{ type: string; strategy_name: string; underlying: string }>;
}

/** Body shape sent to /baskets/import. selections + name_overrides
 *  are keyed by the bundle's strategy index (0..N-1). */
export interface ImportCommitPayload {
  bundle: BasketBundle;
  basket_name_override?: string;
  selections?: Record<string, boolean>;
  name_overrides?: Record<string, string>;
}

class BasketIoService {
  /**
   * Download a portable bundle for the given basket + selected strategies.
   * Triggers a browser download — no return value.
   */
  async exportBasket(basketId: string, strategyIds: string[]): Promise<void> {
    if (!basketId) {
      throw new Error('basketId is required');
    }
    if (!strategyIds.length) {
      throw new Error('At least one strategy must be selected for export');
    }

    const qp = new URLSearchParams({ strategy_ids: strategyIds.join(',') });
    // SuperAdmin-only View-as: backend enforces super_admin_only=True on export.
    withActingAsIfSuperAdmin(qp);
    const bundle = await apiClient.get<BasketBundle>(
      `/baskets/${basketId}/export?${qp.toString()}`,
    );

    const filename = this._filenameFor(bundle);
    const blob = new Blob([JSON.stringify(bundle, null, 2)], {
      type: 'application/json',
    });
    this._triggerDownload(blob, filename);
  }

  /**
   * Validate a parsed bundle and get back per-strategy support flags +
   * a server-suggested unique basket name. No DB writes — safe to call
   * repeatedly while the user iterates on the upload step.
   */
  async previewImport(bundle: BasketBundle): Promise<ImportPreview> {
    const response = await apiClient.post<ApiResponse<ImportPreview>>(
      '/baskets/import/preview',
      { bundle },
    );
    if (!response.data) {
      throw new Error(response.message || 'Failed to preview bundle');
    }
    return response.data;
  }

  /**
   * Atomically write the basket + selected strategies. The `selections`
   * map drops unchecked rows; `name_overrides` rewrite per-strategy
   * names; `basket_name_override` overrides the auto-suggested basket
   * name. Strategies whose underlying isn't in the recipient's tenant
   * are silently skipped server-side regardless of the selections map.
   *
   * SuperAdmin-only View-as: backend enforces super_admin_only=True on
   * /import (mirrors export); appending `?acting_as=<target>` lands the
   * new basket in the target user's partition. Non-SuperAdmin callers
   * send no query param and the import goes to their own partition.
   */
  async commitImport(payload: ImportCommitPayload): Promise<ImportCommitResult> {
    const qp = withActingAsIfSuperAdmin(new URLSearchParams());
    const qs = qp.toString();
    const url = qs ? `/baskets/import?${qs}` : '/baskets/import';
    const response = await apiClient.post<ApiResponse<ImportCommitResult>>(
      url,
      payload,
    );
    if (!response.data) {
      throw new Error(response.message || 'Failed to import bundle');
    }
    return response.data;
  }

  /** Read a JSON file and return its parsed object. Throws on parse
   *  failure or oversized files (>1 MB). Used by the Upload step
   *  before calling previewImport. */
  async readBundleFile(file: File): Promise<BasketBundle> {
    if (file.size > 1024 * 1024) {
      throw new Error(`Bundle file too large (${Math.round(file.size / 1024)} KB; max 1024 KB)`);
    }
    const text = await file.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('File is not valid JSON');
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Bundle file must contain a JSON object');
    }
    return parsed as BasketBundle;
  }

  /** "Iron Condor" + 2026-05-08 → "Iron-Condor-20260508.json" */
  private _filenameFor(bundle: BasketBundle): string {
    const stem = (bundle.basket?.basket_name || 'basket')
      .toString()
      .trim()
      .replace(/[^A-Za-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'basket';
    const date = new Date();
    const yyyymmdd = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
    return `${stem}-${yyyymmdd}.json`;
  }

  private _triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke after a tick so Safari has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

const basketIoService = new BasketIoService();
export default basketIoService;
